// components/PopupModal.jsx
import React, { useState, useEffect } from "react";
import { Trash, Plus } from "lucide-react";

const PopupModal = ({ isOpen, onClose, title, items, type }) => {
  const [isAddingBlindSpot, setIsAddingBlindSpot] = useState(false);
  const [isAddingBarrier, setIsAddingBarrier] = useState(false);

  const [newBlindSpot, setNewBlindSpot] = useState({
    label: "",
    explanation: "",
  });
  const [newBarrier, setNewBarrier] = useState({
    label: "",
    explanation: "",
    surfaced_through: "Internal",
    surfaced_from: "", // <-- new field added
  });

  // Local state for editable blind spot items
  const [blindSpotItems, setBlindSpotItems] = useState([]);
  const [barrierItems, setBarrierItems] = useState([]);

  const [isAddingStake, setIsAddingStake] = useState(false);

  const [newStake, setNewStake] = useState({
    stakeName: "",
    implication: "",
    riskType: "Recoverable", // now this holds the visible dropdown
  });

  const [stakeItems, setStakeItems] = useState([]);

  // Initialize local state when modal opens or items change
  useEffect(() => {
    if (type === "blind_spots") {
      setBlindSpotItems(items || []);
    } else if (type === "barriers") {
      setBarrierItems(items || []);
    } else if (type === "stakes") {
      setStakeItems(items || []);
    }
  }, [items, type, isOpen]);

  // Delete item
  const handleDelete = (itemToDelete) => {
    if (type === "blind_spots") {
      setBlindSpotItems((prev) => prev.filter((item) => item !== itemToDelete));
    } else if (type === "barriers") {
      setBarrierItems((prev) => prev.filter((item) => item !== itemToDelete));
    }
  };

  // Save new blind spot
  const handleSaveBlindSpot = () => {
    if (!newBlindSpot.label.trim() || !newBlindSpot.explanation.trim()) return;

    setBlindSpotItems((prev) => [...prev, newBlindSpot]);
    setNewBlindSpot({ label: "", explanation: "" });
    setIsAddingBlindSpot(false);
  };

  // Inside PopupModal component, after all useState and useEffect hooks
  const displayedItems =
    type === "blind_spots"
      ? blindSpotItems
      : type === "barriers"
      ? barrierItems
      : type === "stakes"
      ? stakeItems
      : items;

  const handleSaveBarrier = () => {
    if (
      !newBarrier.label.trim() ||
      !newBarrier.explanation.trim() ||
      !newBarrier.surfaced_from.trim()
    )
      return;

    setBarrierItems((prev) => [...prev, newBarrier]);
    setNewBarrier({
      label: "",
      explanation: "",
      surfaced_through: "Internal",
      surfaced_from: "",
    });
    setIsAddingBarrier(false);
  };

  const handleSaveStake = () => {
    if (
      !newStake.stakeName.trim() ||
      !newStake.implication.trim() ||
      !newStake.riskType.trim()
    )
      return;

    setStakeItems((prev) => [...prev, newStake]);
    setNewStake({
      stakeName: "",
      implication: "",
      riskType: "Recoverable",
    });

    setIsAddingStake(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-lg p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-7 w-7 flex justify-center items-center bg-gray-200 rounded-full text-[#484848] hover:text-black text-base font-bold"
        >
          ✕
        </button>

        <h2 className="text-xl md:text-2xl font-bold text-[#1D1D1D] font-gotham mb-4 text-center">
          {title}
        </h2>

        <div className="space-y-4 pb-4">
          {displayedItems?.length > 0 ? (
            displayedItems
              .slice()
              .reverse()
              .map((item, idx) => (
                <div
                  key={idx}
                  className="border rounded-lg p-4 bg-gradient-to-b from-[#FFFFFF] to-[#D7EDED] text-sm md:text-base"
                >
                  {type === "blind_spots" && (
                    <>
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-semibold text-[#1D1D1D]">
                          {item.label}
                        </div>
                        <button
                          onClick={() => handleDelete(item)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-[#484848] font-gotham text-base">
                        {item.explanation}
                      </p>
                    </>
                  )}

                  {type === "hero" && (
                    <div className="text-[#484848] font-gotham text-base">
                      {item.split("\n\n").map((paragraph, idx) => (
                        <p
                          key={idx}
                          className="mb-4 text-left text-base md:text-lg text-[#1D1D1D]"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  )}

                  {type === "barriers" && (
                    <>
                      {item.label && (
                        <>
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-bold font-gotham text-lg text-[#1D1D1D]">
                              {item.label}
                            </div>
                            <button
                              onClick={() => handleDelete(item)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-[#484848] font-medium font-gotham text-base mb-1">
                            {item.explanation}
                          </p>
                          {/* <p className="text-gray-400 font-medium font-gotham text-sm italic">
                          Surfaced Through: {item.surfaced_through}
                        </p> */}
                        </>
                      )}
                    </>
                  )}

                  {type === "stakes" && (
                    <>
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-bold font-gotham text-lg text-[#1D1D1D]">
                          {item.stakeName}
                        </div>
                        <button
                          onClick={() => handleDelete(item)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-[#484848] font-medium font-gotham text-base mb-1">
                        {item.implication}
                      </p>
                      <p className="text-gray-400 font-medium font-gotham text-sm italic">
                        Risk Level: {item.riskType}
                      </p>
                    </>
                  )}
                </div>
              ))
          ) : (
            <p className="text-center text-gray-500">No data available</p>
          )}
        </div>

        {/* Add Form Only for stakes */}
        {type === "stakes" && (
          <div className="mt-4">
            {!isAddingStake ? (
              <button
                onClick={() => setIsAddingStake(true)}
                className="flex items-center gap-2 bg-[#54B0AF] text-white px-4 py-2 rounded-[80px] text-sm md:text-base font-medium hover:bg-[#469a9a] transition-all"
              >
                <Plus className="w-4 h-4" />
                Add New
              </button>
            ) : (
              <div className="bg-gray-50 p-4 rounded-xl space-y-3 mt-4">
                <input
                  type="text"
                  placeholder="Stake Label"
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                  value={newStake.stakeName}
                  onChange={(e) =>
                    setNewStake({ ...newStake, stakeName: e.target.value })
                  }
                />
                <textarea
                  placeholder="Implication"
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                  rows={3}
                  value={newStake.implication}
                  onChange={(e) =>
                    setNewStake({ ...newStake, implication: e.target.value })
                  }
                />
                <select
                  value={newStake.riskType}
                  onChange={(e) =>
                    setNewStake({ ...newStake, riskType: e.target.value })
                  }
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                >
                  <option value="Recoverable">Recoverable</option>
                  <option value="Critical">Critical</option>
                </select>
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveStake}
                    className="bg-[#54B0AF] text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    ✅ Save
                  </button>
                  <button
                    onClick={() => {
                      setNewStake({
                        stakeName: "",
                        implication: "",
                        riskType: "Recoverable",
                      });
                      setIsAddingStake(false);
                    }}
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Form Only for barriers  */}
        {type === "barriers" && (
          <div className="mt-4">
            {!isAddingBarrier ? (
              <button
                onClick={() => setIsAddingBarrier(true)}
                className="flex items-center gap-2 bg-[#54B0AF] text-white px-4 py-2 rounded-[80px] text-sm md:text-base font-medium hover:bg-[#469a9a] transition-all"
              >
                <Plus className="w-4 h-4" />
                Add New
              </button>
            ) : (
              <div className="bg-gray-50 p-4 rounded-xl space-y-3 mt-4">
                {/* Add this input for label */}
                <input
                  type="text"
                  placeholder="Label"
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                  value={newBarrier.label}
                  onChange={(e) =>
                    setNewBarrier({
                      ...newBarrier,
                      label: e.target.value,
                    })
                  }
                />

                <input
                  type="text"
                  placeholder="Surfaced From"
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                  value={newBarrier.surfaced_from}
                  onChange={(e) =>
                    setNewBarrier({
                      ...newBarrier,
                      surfaced_from: e.target.value,
                    })
                  }
                />

                <textarea
                  placeholder="Explanation"
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                  rows={3}
                  value={newBarrier.explanation}
                  onChange={(e) =>
                    setNewBarrier({
                      ...newBarrier,
                      explanation: e.target.value,
                    })
                  }
                />
                <select
                  value={newBarrier.surfaced_through}
                  onChange={(e) =>
                    setNewBarrier({
                      ...newBarrier,
                      surfaced_through: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                >
                  <option value="Internal">Internal Barrier</option>
                  <option value="External">External Barrier</option>
                </select>
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveBarrier}
                    className="bg-[#54B0AF] text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    ✅ Save
                  </button>
                  <button
                    onClick={() => {
                      setNewBarrier({
                        label: "",
                        explanation: "",
                        surfaced_through: "Internal",
                        surfaced_from: "",
                      });

                      setIsAddingBarrier(false);
                    }}
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Form Only for Blind Spots */}
        {type === "blind_spots" && (
          <div className="mt-4">
            {!isAddingBlindSpot ? (
              <button
                onClick={() => setIsAddingBlindSpot(true)}
                className="flex items-center gap-2 bg-[#54B0AF] text-white px-4 py-2 rounded-[80px] text-sm md:text-base font-medium hover:bg-[#469a9a] transition-all"
              >
                <Plus className="w-4 h-4" />
                Add New
              </button>
            ) : (
              <div className="bg-gray-50 p-4 rounded-xl space-y-3 mt-4">
                <input
                  type="text"
                  placeholder="Label"
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                  value={newBlindSpot.label}
                  onChange={(e) =>
                    setNewBlindSpot({
                      ...newBlindSpot,
                      label: e.target.value,
                    })
                  }
                />
                <textarea
                  placeholder="Explanation"
                  className="w-full border border-gray-300 px-3 py-2 rounded-md"
                  rows={3}
                  value={newBlindSpot.explanation}
                  onChange={(e) =>
                    setNewBlindSpot({
                      ...newBlindSpot,
                      explanation: e.target.value,
                    })
                  }
                />
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveBlindSpot}
                    className="bg-[#54B0AF] text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    ✅ Save
                  </button>
                  <button
                    onClick={() => {
                      setNewBlindSpot({ label: "", explanation: "" });
                      setIsAddingBlindSpot(false);
                    }}
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PopupModal;
